import { message } from 'antd';
import { searchRoutesV2, searchVehicleTypesV2 } from 'store/vendorSliceV2';
import { searchVehiclesV2 } from 'store/vehicleSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { extractFilenameFromS3Key } from './VendorContractFormV2.constants';

/**
 * Handle file upload to S3 using signed URL
 * @param {File} file - The file to upload
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setFileUploading - State setter for upload status
 * @param {Function} setUploadedFileName - State setter for file name
 * @param {Function} setUploadedFileSize - State setter for file size
 * @param {Function} setUploadedFileUrl - State setter for file URL
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @returns {Promise<void>}
 */
export const handleFileUpload = async (
	file,
	dispatch,
	setFileUploading,
	setUploadedFileName,
	setUploadedFileSize,
	setUploadedFileUrl,
	setFieldValue
) => {
	if (!file) return;

	try {
		setFileUploading(true);

		// Get signed URL
		const signedUrlResponse = await dispatch(
			getSignedUrl({
				url_type: 'upload',
				data: {
					entity: 'vendor_contract',
					keys: [file.name],
				},
			})
		);

		if (!signedUrlResponse?.data?.[0]?.url) {
			throw new Error('Failed to get signed URL');
		}

		const signedUrlData = signedUrlResponse.data[0];

		// Upload file to S3
		const uploadResponse = await fetch(signedUrlData.url, {
			method: 'PUT',
			body: file,
		});

		if (!uploadResponse.ok) {
			throw new Error('Failed to upload file');
		}

		// Get the uploaded file URL (use the filename from response)
		const uploadedUrl = signedUrlData.filename;

		// Extract clean filename
		const cleanFileName = extractFilenameFromS3Key(file.name);

		// Update all file-related states
		setUploadedFileName(cleanFileName);
		setUploadedFileSize(file.size);
		setUploadedFileUrl(uploadedUrl);
		setFieldValue('signed_contract_file', uploadedUrl);

		message.success('File uploaded successfully');
	} catch (error) {
		console.error('File upload error:', error);
		message.error('Failed to upload file');
	} finally {
		setFileUploading(false);
	}
};

/**
 * Fetch routes based on project and vehicle type
 * @param {number} projectId - The project ID
 * @param {number} vehicleTypeId - The vehicle type ID
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setFilteredRoutes - State setter for filtered routes
 * @returns {Promise<void>}
 */
export const handleRouteFetch = async (projectId, vehicleTypeId, dispatch, setFilteredRoutes) => {
	if (!projectId || !vehicleTypeId) {
		setFilteredRoutes([]);
		return;
	}

	try {
		const routesResponse = await dispatch(
			searchRoutesV2({
				filter_by: {
					project_id: Number(projectId),
					vehicle_type_id: Number(vehicleTypeId),
					is_active: true,
				},
				limit: 1000,
			})
		);

		const routes =
			routesResponse?.rows?.map((item) => ({
				value: item.id,
				label: item.route_name || '-',
			})) || [];

		setFilteredRoutes(routes);
	} catch (error) {
		console.error('Error fetching routes:', error);
		setFilteredRoutes([]);
	}
};

/**
 * Fetch vehicles for a given route_id
 * @param {number} routeId - Route ID to filter vehicles
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setVehicleOptions - State setter for vehicle options
 * @param {Array} existingVehicles - Pre-existing vehicles to merge (edit mode)
 * @returns {Promise<void>}
 */
export const handleVehicleFetch = async (
	routeId,
	dispatch,
	setVehicleOptions,
	existingVehicles = []
) => {
	if (!routeId) {
		setVehicleOptions([]);
		return;
	}

	try {
		const response = await dispatch(
			searchVehiclesV2({
				filter_by: { route_id: Number(routeId) },
				pagination: { page: 1, per_page: 1000 },
			})
		);

		const fetched = (response?.rows || []).map((v) => ({
			value: v.id ?? v.value,
			label: v.vehicle_number ?? v.label ?? `Vehicle ${v.id}`,
		}));

		// Merge any pre-existing vehicles (for edit mode) that may not match the route filter
		const existingIds = new Set(fetched.map((o) => o.value));
		const extras = existingVehicles
			.filter((v) => !existingIds.has(v.id ?? v.value))
			.map((v) => ({
				value: v.id ?? v.value,
				label: v.vehicle_number ?? v.label ?? `Vehicle ${v.id}`,
			}));

		setVehicleOptions([...fetched, ...extras]);
	} catch (error) {
		console.error('Error fetching vehicles by route:', error);
		setVehicleOptions([]);
	}
};

/**
 * Fetch vehicle types based on temperature controlled filter
 * @param {boolean} temperatureControlled - Temperature controlled filter value
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setFilteredVehicleTypes - State setter for filtered vehicle types
 * @param {Function} setVehicleTypesFullData - State setter for full vehicle types data
 * @returns {Promise<void>}
 */
export const handleVehicleTypeFetch = async (
	temperatureControlled,
	dispatch,
	setFilteredVehicleTypes,
	setVehicleTypesFullData
) => {
	if (temperatureControlled === undefined || temperatureControlled === null) {
		setFilteredVehicleTypes([]);
		setVehicleTypesFullData([]);
		return;
	}

	try {
		const vehicleTypesResponse = await dispatch(
			searchVehicleTypesV2({
				filter_by: {
					temperature_controlled: Boolean(temperatureControlled),
				},
			})
		);

		const vehicleTypesData = vehicleTypesResponse?.rows || [];
		setVehicleTypesFullData(vehicleTypesData);

		const vehicleTypes = vehicleTypesData.map((item) => ({
			value: item.id,
			label: item.vehicle_type_name || '-',
		}));

		setFilteredVehicleTypes(vehicleTypes);
	} catch (error) {
		console.error('Error fetching vehicle types:', error);
		setFilteredVehicleTypes([]);
		setVehicleTypesFullData([]);
	}
};

/**
 * Format file size to human readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
	if (!bytes) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};
